import { useAuth } from '@/hooks';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import {
  Avatar,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Button,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { KText } from '@/ui-library/components';
import { useKFlags } from '@/hooks/use-k-flags';
import { ThemeToggle, useIsDarkModeEnabled } from '@/theme/colorMode';

// Every row the same height (Figma Settings 13039:44281: 44px), so the Theme
// row's icon tabs don't make it taller than the rest.
const MENU_ROW_HEIGHT = '44px';

interface CurrentUser {
  first_name?: string;
  last_name?: string;
  avatar?: string;
}

interface UseAuthReturn {
  currUser: CurrentUser | null;
  logout: () => Promise<void>;
}

export const UserProfile = () => {
  const { currUser: user, logout } = useAuth() as UseAuthReturn;
  const history = useHistory();
  // usePermissionGuard spreads dynamic permission keys into its return object;
  // TS can only see the literal `permissions` field, so cast to a Record for
  // the dynamic flags we read.
  const guard = usePermissionGuard() as Record<string, boolean> & {
    permissions: unknown;
  };
  const isAccountInviteEnabled = Boolean(guard.isAccountInviteEnabled);
  const { ats_enabled } = useKFlags();
  const isDarkModeEnabled = useIsDarkModeEnabled();

  const onLogout = async () => {
    await logout();
  };

  const renderTrigger = () => (
    <Avatar
      name={`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()}
      src={user?.avatar}
      width="24px"
      height="24px"
      className="avatar-md"
    />
  );

  return (
    <Menu>
      {({ onClose }) => (
        <>
          <MenuButton
            as={Button}
            p="0px !important"
            minW="24px"
            h="24px"
            border="none"
            bg="none"
            _hover={{ bg: 'none' }}
            _active={{ bg: 'none' }}
            data-test-id="user-profile"
          >
            {renderTrigger()}
          </MenuButton>
          <MenuList
            p="0px !important"
            // Figma Settings 13039:44281: 270px, room for the Theme icon tabs.
            width="270px"
            borderRadius="md"
            overflow="hidden"
          >
            {ats_enabled && (
              <Flex
                data-test-id="profile-settings"
                px="md"
                h={MENU_ROW_HEIGHT}
                alignItems="center"
                cursor="pointer"
                onClick={() => {
                  history.push('/settings/profile-settings');
                  onClose();
                }}
                borderBottom="1px solid"
                borderColor="border-color"
                _hover={{ bg: 'secondary-25' }}
                transition="background-color 0.2s"
              >
                <KText size="sm-medium" color="secondary-700">
                  Profile settings
                </KText>
              </Flex>
            )}
            {!ats_enabled && isAccountInviteEnabled && (
              <Flex
                data-test-id="invite-teammates"
                px="md"
                h={MENU_ROW_HEIGHT}
                alignItems="center"
                cursor="pointer"
                onClick={() => {
                  history.push('/settings/users-roles');
                  onClose();
                }}
                borderBottom="1px solid"
                borderColor="border-color"
                _hover={{ bg: 'secondary-25' }}
                transition="background-color 0.2s"
              >
                <KText size="sm-medium" color="secondary-700">
                  Invite teammates
                </KText>
              </Flex>
            )}
            <Flex
              data-test-id="notifications-center"
              px="md"
              h={MENU_ROW_HEIGHT}
              alignItems="center"
              cursor="pointer"
              onClick={() => {
                history.push('/settings/notifications-center');
                onClose();
              }}
              borderBottom="1px solid"
              borderColor="border-color"
              _hover={{ bg: 'secondary-25' }}
              transition="background-color 0.2s"
            >
              <KText size="sm-medium" color="secondary-700">
                Notifications center
              </KText>
            </Flex>
            {isDarkModeEnabled && (
              <Flex
                data-test-id="theme-row"
                px="md"
                h={MENU_ROW_HEIGHT}
                alignItems="center"
                justifyContent="space-between"
                gap="sm"
                borderBottom="1px solid"
                borderColor="border-color"
              >
                <KText size="sm-medium" color="secondary-700">
                  Theme
                </KText>
                <ThemeToggle />
              </Flex>
            )}
            <Flex
              data-test-id="logout"
              px="md"
              h={MENU_ROW_HEIGHT}
              alignItems="center"
              cursor="pointer"
              onClick={() => {
                onLogout();
                onClose();
              }}
              _hover={{ bg: 'secondary-25' }}
              transition="background-color 0.2s"
            >
              <KText size="sm-medium" color="secondary-700">
                Logout
              </KText>
            </Flex>
          </MenuList>
        </>
      )}
    </Menu>
  );
};
